import React, { useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { MaterialReactTable, useMaterialReactTable, MRT_ToggleFiltersButton } from "material-react-table";
import { Autocomplete, Box, Checkbox, Chip, Collapse, IconButton, TextField, Tooltip, InputAdornment, TableCell } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import SearchOff from "@mui/icons-material/SearchOff";
import ClearIcon from "@mui/icons-material/Clear";
import moment from "moment";
import "./UKETSAerListingPage.scss";
import { Button, GenericDropdown, Icon, ICONS, Label, Modal, COLORS } from "abs-ui-components";
import BreadCrumb from "../../common/breadCrumb/BreadCrumb";
import Loader from "../../common/Loader/Loader";
import { emitToasterMsg } from "../../services/toasterUtility";
import { ClaimsContext } from "../../services/ClaimsService";
import { ClaimType } from "../../constants/claims";
import { getYearOptions } from "../../services/utilities";
import { useSelector } from "react-redux";
import { getUKETSAERList, downloadUKETSAERDeliverable, assignUKETSAER, claimUKETSAER, unclaimUKETSAER, deleteUKETSAER } from "../../services/UKETSAerService";
import { UKETSAER_TABLE_COLUMNS } from "./UKETSAerListingColumns";
import CreateUKETSAerModal from "../../components/UKETSAnnualEmissionReport/CreateUKETSAerModal";
import AutocompleteDropdown from "../../common/autocompleteDropdown/AutocompleteDropdown";
import { accessAPIForResponse } from "../../services/api-services/api-implementations";
import ColumnFilterTextField from "../Reports/reports-page/ColumnFilterTextField";
import AbsModal from "../../common/absModal/AbsModal";

/* -------------------------------------------------------------------------- */
/*                               CONSTANTS                                    */
/* -------------------------------------------------------------------------- */

const TEXT_FILTER_COLUMNS = ["companyImoNumber", "companyName", "wcnNumber", "wcnCompanyName"];

const UKETSAER_STATUSES = [
	"No Data Submitted",
	"Supporting Documents Needed",
	"Crosscheck In Progress",
	"Verification In Progress",
	"Under Revision",
	"Verification Completed",
];

/* -------------------------------------------------------------------------- */
/*                         NAVIGABLE TABLE CELL                               */
/* -------------------------------------------------------------------------- */

const UKETSTableRow = ({ ukEtsAerId, column, ...props }) => {
	const nonLinkColumns = ["actionBtnName", "download", "deleteAction", "mrt-row-expand"];
	const isLinkColumn = !nonLinkColumns.includes(column?.id);
	var id = parseInt(ukEtsAerId?.split("-").pop());
	return (
		<TableCell {...props}>
			{isLinkColumn ? (
				<a
					href={`/uk-ets-annual-emission-report/${id}`}
					style={{
						textDecoration: "none",
						color: "inherit",
						display: "block",
						width: "100%",
					}}
				>
					{props.children}
				</a>
			) : (
				props.children
			)}
		</TableCell>
	);
};

/* -------------------------------------------------------------------------- */
/*                            GLOBAL SEARCH                                   */
/* -------------------------------------------------------------------------- */

const GlobalFilterTextField = React.forwardRef(({ searchInput, applyGlobalSearch, autoFocus }, ref) => {
	const [value, setValue] = useState(searchInput ?? "");
	const inputRef = useRef(null);

	useEffect(() => {
		setValue(searchInput ?? "");
	}, [searchInput]);

	useImperativeHandle(ref, () => ({
		getValue: () => value,
		clear: () => setValue(""),
		focus: () => inputRef.current?.focus(),
	}));

	return (
		<TextField
		inputRef={inputRef}
			autoFocus={autoFocus}
			size="small"
			variant="outlined"
			placeholder="Type and Search"
			value={value}
			onChange={(e) => {
				const newValue = e.target.value;
				setValue(newValue);
				if (newValue === "" && searchInput) {
					applyGlobalSearch("");
				}
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					applyGlobalSearch(value);
				}
			}}
			InputProps={{
				startAdornment: (
					<InputAdornment position="start">
						<SearchIcon fontSize="small" />
					</InputAdornment>
				),
				endAdornment: value && (
					<InputAdornment position="end">
						<IconButton
							size="small"
							onClick={() => {
								setValue("");
								applyGlobalSearch("");
							}}
						>
							<ClearIcon fontSize="small" />
						</IconButton>
					</InputAdornment>
				),
			}}
		/>
	);
});

GlobalFilterTextField.displayName = "GlobalFilterTextField";

/* -------------------------------------------------------------------------- */
/*                           MULTISELECT FILTER                               */
/* -------------------------------------------------------------------------- */

const MultiSelectFilter = React.memo(({ columnId, options, placeholder, columnWidth, getFilterValue, updateFilterValue }) => {
	return (
		<Autocomplete
			multiple
			options={options}
			disableCloseOnSelect
			value={getFilterValue(columnId) || []}
			isOptionEqualToValue={(option, value) => option === value}
			onChange={(e, newValue) => {
				updateFilterValue(columnId, newValue);
			}}
			size="small"
			renderOption={(props, option, { selected }) => (
				<li {...props} key={option}>
					<Checkbox checked={selected} />
					{option}
				</li>
			)}
			renderTags={(value) => (
				<Chip
					label={value.length > 1 ? `${value.length} ${columnId}s selected` : value[0]}
					size="small"
					sx={{
						backgroundColor: "gray",
						color: "white",
						"& .MuiChip-deleteIcon": {
							color: "white",
						},
					}}
				/>
			)}
			renderInput={(params) => <TextField {...params} variant="standard" placeholder={placeholder} />}
			sx={{
				width: columnWidth || "auto",
				"& .MuiInputBase-input": { color: "white" },
				"& .MuiSvgIcon-root": { color: "white" },
				"& .MuiInput-underline:before": { borderBottomColor: "white" },
				"& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: "white" },
				"& .MuiInput-underline:after": { borderBottomColor: "white" },
			}}
		/>
	);
});

/* -------------------------------------------------------------------------- */
/*                           HIGHLIGHTED CELL                                 */
/* -------------------------------------------------------------------------- */

const HighlightedCell = ({ value, columnId, columnFilters, user }) => {
	const currentFilter = columnFilters.find((f) => f.id === columnId);
	const selectedValues = currentFilter?.value || [];

	const normalizedValues = selectedValues.map((item) => {
		if (item === "@Me") return user?.email;
		if (item === "@Unassigned") return "";
		return item;
	});

	const isMatched = Array.isArray(normalizedValues) && normalizedValues.includes(value);

	return (
		<span
			style={{
				backgroundColor: isMatched ? "rgb(255, 203, 127)" : "transparent",
				color: isMatched ? "black" : "inherit",
				padding: "2px 4px",
				borderRadius: "4px",
			}}
		>
			{value}
		</span>
	);
};

/* -------------------------------------------------------------------------- */
/*                              MAIN PAGE                                     */
/* -------------------------------------------------------------------------- */

const UKETSAerListingPage = () => {
	const { userHasClaim } = useContext(ClaimsContext);
	const user = useSelector((state) => state.userInfo.userData);
	const currentYear = moment().year();

	/* ---------------------------------------------------------------------- */
	/*                              YEAR                                      */
	/* ---------------------------------------------------------------------- */

	const [yearOptions, setYearOptions] = useState([]);
	const storedYear = localStorage.getItem("uketsaer-selectedYear") || String(currentYear);
	const [selectedYear, setSelectedYear] = useState([storedYear]);

	/* ---------------------------------------------------------------------- */
	/*                              TABLE                                     */
	/* ---------------------------------------------------------------------- */

	const [reportedData, setReportedData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [rowCount, setRowCount] = useState(0);
	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: 25,
	});

	const [sorting, setSorting] = useState([]);
	const [columnFilters, setColumnFilters] = useState([]);
	const [pendingColumnFilters, setPendingColumnFilters] = useState([]);
	const [globalFilter, setGlobalFilter] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [showSearch, setShowSearch] = useState(false);
	const globalSearchRef = useRef(null);
	const [deleteRecord, setDeleteRecord] = useState(null);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const textFilterRefs = useRef({});
	const [engineersOptions, setEngineersOptions] = useState([]);

	/* ---------------------------------------------------------------------- */
	/*                              LOADERS                                   */
	/* ---------------------------------------------------------------------- */

	const [pageLoader, setPageLoader] = useState(false);
	const fetchListRef = useRef(null);

	/* ---------------------------------------------------------------------- */
	/*                              MODALS                                    */
	/* ---------------------------------------------------------------------- */

	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showAssignModal, setShowAssignModal] = useState(false);
	const [selectedRecord, setSelectedRecord] = useState(null);
	const [actionType, setActionType] = useState("");
	const [selectedEngineer, setSelectedEngineer] = useState("");
	const [disableAssignBtn, setDisableAssignBtn] = useState(true);


	const getDeleteState = ({
	reviewerRole,
	verifierRole,
	superAdminRole,
	status,
}) => {
	const isVerificationCompleted =
		status === "Verification Completed";

	if (superAdminRole) {
		return {
			show: true,
			disabled: false,
		};
	}
	if(isVerificationCompleted){
		return {
			show: false,
		};
	}
	if(!(reviewerRole || verifierRole || superAdminRole) && status !== "No Data Submitted") {
      return {
			show: false,
		};
	}                            

	return {
		show: true,
		disabled: isVerificationCompleted,
	};
};

	/* ---------------------------------------------------------------------- */
	/*                            BREADCRUMB                                  */
	/* ---------------------------------------------------------------------- */

	const breadcrumb = useMemo(
		() => [
			{
				label: "Annual Emission Verification",
				url: "",
				navId: "annual_emission_verification_navid",
			},
			{
				label: "UK ETS Annual Emission Report",
				url: "",
				navId: "uketsaer_listing_navid",
			},
		],
		[]
	);

	/* ---------------------------------------------------------------------- */
	/*                         YEAR OPTIONS                                   */
	/* ---------------------------------------------------------------------- */

	useEffect(() => {
		const opts = getYearOptions(false);
		setYearOptions(opts);
	}, []);

	/* ---------------------------------------------------------------------- */
	/*                          FETCH LIST                                    */
	/* ---------------------------------------------------------------------- */

	const fetchList = useCallback(async () => {
		setLoading(true);

		try {
			const sortOption =
				sorting.length > 0
					? {
							sortField: sorting[0].id === "wcnNumber" ? "WCN" : sorting[0].id,
							isAscending: !sorting[0].desc,
						}
					: {
							sortField: "",
							isAscending: true,
						};

			const payload = {
				reportYear: selectedYear[0],
				globalFilter: globalFilter,
				filters: columnFilters.map((filter) => ({
					key: filter.id,
					value: Array.isArray(filter.value)
						? filter.value.map((item) => {
								if (item === "@Me") return user?.email;
								if (item === "@Unassigned") return "";
								return item;
							})
						: filter.value,
				})),

				pagination: {
					page: pagination.pageIndex + 1,
					pageSize: pagination.pageSize,
				},
				sortOption,
			};

			const response = await getUKETSAERList(payload);
			if (response?.Data) {
				setReportedData(response.Data);
				setRowCount(response.Pagination?.TotalItems || 0);
			} else {
				setReportedData([]);
				setRowCount(0);
			}
		} catch (err) {
			console.error("Error loading UK ETS AER list:", err);
			emitToasterMsg("error", "Error", "Failed to load UK ETS AER list.");
		} finally {
			setLoading(false);
		}
	}, [selectedYear, pagination, sorting, columnFilters, globalFilter, user]);

	useEffect(() => {
		fetchListRef.current = fetchList;
	});

	/* ---------------------------------------------------------------------- */
	/*                       FETCH ON TABLE STATE CHANGE                      */
	/* ---------------------------------------------------------------------- */

	useEffect(() => {
		fetchList();
	}, [fetchList]);

	/* ---------------------------------------------------------------------- */
	/*                            YEAR CHANGE                                 */
	/* ---------------------------------------------------------------------- */

	const onYearChange = (selected) => {
		setSelectedYear(selected);
		localStorage.setItem("uketsaer-selectedYear", selected[0]);
		setPagination((prev) => ({
			...prev,
			pageIndex: 0,
		}));
	};

	/* ---------------------------------------------------------------------- */
	/*                         GLOBAL SEARCH                                  */
	/* ---------------------------------------------------------------------- */

	const applyGlobalSearch = useCallback((value) => {
		setSearchInput(value);
		setGlobalFilter(value);

		setPagination((prev) => ({
			...prev,
			pageIndex: 0,
		}));
	}, []);

	/* ---------------------------------------------------------------------- */
	/*                        COLUMN FILTER                                  */
	/* ---------------------------------------------------------------------- */

	const applyColumnFilter = useCallback((columnId, value) => {
		setPagination((prev) => ({
			...prev,
			pageIndex: 0,
		}));

		const isCleared = value == null || value === "" || (Array.isArray(value) && value.length === 0);

		setColumnFilters((previousFilters) => {
			const filters = [...previousFilters];

			const index = filters.findIndex((filter) => filter.id === columnId);

			if (isCleared) {
				if (index >= 0) {
					filters.splice(index, 1);
				}
			} else {
				if (index >= 0) {
					filters[index] = {
						id: columnId,
						value,
					};
				} else {
					filters.push({
						id: columnId,
						value,
					});
				}
			}

			return filters;
		});
	}, []);

	/* ---------------------------------------------------------------------- */
	/*                         APPLY FILTERS                                  */
	/* ---------------------------------------------------------------------- */
	
	const applyFilters = useCallback(() => {
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));

		// Collect text column filter values from refs
		const textFilters = Object.entries(textFilterRefs.current)
			.map(([columnId, ref]) => ({
				id: columnId,
				value: ref.getValue?.() ?? "",
			}))
			.filter((filter) => filter.value != null && String(filter.value).trim() !== "");

		// Merge text filters with pending multiselect filters
		const mergedFilters = [...textFilters, ...pendingColumnFilters];
		setColumnFilters(mergedFilters);

		// Sync global search from ref
		const globalValue = globalSearchRef.current?.getValue() ?? "";
		setSearchInput(globalValue);
		setGlobalFilter(globalValue);
	}, [pendingColumnFilters]);

	/* ---------------------------------------------------------------------- */
	/*                        CLEAR ALL FILTERS                               */
	/* ---------------------------------------------------------------------- */

	const clearAllFilters = useCallback(() => {
		setColumnFilters([]);
		setPendingColumnFilters([]);
		setGlobalFilter("");
		setSearchInput("");

		setPagination((prev) => ({
			...prev,
			pageIndex: 0,
		}));

		Object.values(textFilterRefs.current).forEach((ref) => {
			ref?.clear?.(false);
		});

		globalSearchRef.current?.clear?.();
	}, []);

	/* ---------------------------------------------------------------------- */
	/*                             DOWNLOAD                                   */
	/* ---------------------------------------------------------------------- */

	const handleDownload = async (event, record) => {
		event.stopPropagation();

		setPageLoader(true);

		try {
			const uketsReportId = parseInt(record.ukEtsAerId?.split("-").pop());
			const blob = await downloadUKETSAERDeliverable(uketsReportId);

			if (blob) {
				const url = window.URL.createObjectURL(
					new Blob([blob], {
						type: blob?.type,
					})
				);

				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download",  "UK ETS Annual Emission Report.zip");
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
				emitToasterMsg("success", "Downloaded successfully.");
			}
		} catch (err) {
			console.error("Download failed:", err);

			emitToasterMsg("error", "Download Failed", "Unable to download the report.");
		} finally {
			setPageLoader(false);
		}
	};

	/* ---------------------------------------------------------------------- */
	/*                       ACTION BUTTON LABEL                              */
	/* ---------------------------------------------------------------------- */

	const getActionButtonLabel = (row) => {
		if (row.showClaimButton) return "Claim";
		if (row.showReassignButton) return "Re-Assign";
		if (row.showUnclaimButton) return "Un-Claim";
		if (row.showAssignButton) return "Assign";

		return null;
	};

	/* ---------------------------------------------------------------------- */
	/*                         ACTION CLICK                                   */
	/* ---------------------------------------------------------------------- */

	const handleActionClick = (record, btnLabel) => {
		if (!btnLabel) {
			return;
		}
		setSelectedRecord(record);
		setActionType(btnLabel);

		if (btnLabel === "Claim" || btnLabel === "Un-Claim") {
			handleAssignClaim(record, btnLabel, user?.email);
		} else {
			setSelectedEngineer("");
			setDisableAssignBtn(true);
			setShowAssignModal(true);
		}
	};

	const handleDeleteClick = (event, record) => {
		event.stopPropagation();
		setDeleteRecord(record);
		setShowDeleteModal(true);
	};

	const handleDeleteConfirm = async () => {
		const uketsReportId = parseInt(deleteRecord.ukEtsAerId?.split("-").pop());
		if (!uketsReportId) {
			return;
		}

		setDeleteLoading(true);

		try {
			const response = await deleteUKETSAER(uketsReportId);

			if (response?.isSuccess || response?.IsSuccess) {
				emitToasterMsg("success", "Deleted successfully.");
				setShowDeleteModal(false);
				setDeleteRecord(null);
				fetchListRef.current();
			} else {
				emitToasterMsg("error", "Delete Failed", response?.errorMessage || response?.ErrorMessage || "Unable to delete the UK ETS report.");
			}
		} catch (error) {
			console.error("Error deleting UK ETS report:", error);
			emitToasterMsg("error", "Delete Failed", "Unable to delete the UK ETS report.");
		} finally {
			setDeleteLoading(false);
		}
	};
	/* ---------------------------------------------------------------------- */
	/*                      CLAIM / ASSIGN API                               */
	/* ---------------------------------------------------------------------- */
	const handleAssignClaim = async (record, btnLabel, engineerEmail) => {
		const rec = record || selectedRecord;

		if (!rec) {
			return;
		}

		try {
			let result;
			const uketsCompanyLevelReportId = parseInt(rec.ukEtsAerId?.split("-").pop());
			if (btnLabel === "Claim") {
				result = await claimUKETSAER(uketsCompanyLevelReportId);
			} else if (btnLabel === "Un-Claim") {
				result = await unclaimUKETSAER(uketsCompanyLevelReportId);
			} else {
				result = await assignUKETSAER(uketsCompanyLevelReportId, engineerEmail);
			}

			if (result?.isSuccess) {
				emitToasterMsg("success", "Action completed successfully.");
				setShowAssignModal(false);
				setSelectedRecord(null);
				setSelectedEngineer("");
				setDisableAssignBtn(true);
				fetchListRef.current();
			} else {
				emitToasterMsg("error", "Action Failed", result?.errorMessage || "Unable to complete the action.");
			}
		} catch (error) {
			console.error("Error performing UK ETS action:", error);

			emitToasterMsg("error", "Action Failed", "Unable to complete the action.");
		}
	};

	/* ---------------------------------------------------------------------- */
	/*                        ENGINEER LOOKUP                                */
	/* ---------------------------------------------------------------------- */

	const GetEngineersList = async (input) => {
		try {
			const response = await accessAPIForResponse("GetReviewEngineers", null, [input]);

			if (response) {
				return response.map((obj) => ({
					label: obj.emailAddress,
					value: obj.emailAddress,
				}));
			}

			return [];
		} catch (error) {
			console.error("Error loading engineers:", error);

			return [];
		}
	};

	/* ---------------------------------------------------------------------- */
	/*                     FETCH ALL ENGINEERS (for multiselect)             */
	/* ---------------------------------------------------------------------- */

	const getAllEngineers = useCallback(async () => {
		try {
			const response = await accessAPIForResponse("GetReviewEngineers", null, [""]);
			if (response) {
				setEngineersOptions(response.map((engineer) => engineer.emailAddress));
			} else {
				setEngineersOptions([]);
			}
		} catch (error) {
			console.error("Error fetching engineers:", error);
			setEngineersOptions([]);
		}
	}, []);

	useEffect(() => {
		getAllEngineers();
	}, [getAllEngineers]);

	/* ---------------------------------------------------------------------- */
	/*                   MULTISELECT FILTER VALUE HELPERS                    */
	/* ---------------------------------------------------------------------- */

	const getFilterValue = useCallback(
		(columnId) => {
			const filter = pendingColumnFilters.find((f) => f.id === columnId);
			return filter?.value || [];
		},
		[pendingColumnFilters]
	);

	const updateFilterValue = useCallback((columnId, value) => {
		const isCleared = (Array.isArray(value) && value.length === 0) || value === "" || value == null;

		// update pending filters
		setPendingColumnFilters((prev) => {
			const others = prev.filter((f) => f.id !== columnId);
			return !isCleared ? [...others, { id: columnId, value }] : others;
		});

		// auto-clear applied filter if selection is cleared
		setColumnFilters((prevApplied) => {
			const appliedFilter = prevApplied.find((f) => f.id === columnId);
			const wasApplied =
				appliedFilter &&
				(Array.isArray(appliedFilter.value) ? appliedFilter.value.length > 0 : appliedFilter.value !== "" && appliedFilter.value != null);

			if (isCleared && wasApplied) {
				setPagination((prev) => ({ ...prev, pageIndex: 0 }));
				return prevApplied.filter((f) => f.id !== columnId);
			}
			return prevApplied;
		});
	}, []);

	/* ---------------------------------------------------------------------- */
	/*                         TABLE COLUMNS                                 */
	/* ---------------------------------------------------------------------- */

	const tableColumns = useMemo(() => {
		const dataColumns = UKETSAER_TABLE_COLUMNS.map((column) => {
			if (column.accessorKey === "status") {
				return {
					...column,
					filterVariant: "custom",
					Cell: ({ cell }) => <HighlightedCell value={cell.getValue()} columnId="status" columnFilters={columnFilters} user={user} />,
					Filter: () => (
						<MultiSelectFilter
							columnId="status"
							options={UKETSAER_STATUSES}
							placeholder="Filter Status"
							columnWidth="260px"
							getFilterValue={getFilterValue}
							updateFilterValue={updateFilterValue}
						/>
					),
				};
			}

			if (column.accessorKey === "reviewer") {
				return {
					...column,
					filterVariant: "custom",
					Cell: ({ cell }) => <HighlightedCell value={cell.getValue()} columnId="reviewer" columnFilters={columnFilters} user={user} />,
					Filter: () => (
						<MultiSelectFilter
							columnId="reviewer"
							options={[
								...(user?.roles?.some((role) => role === "ER_REVIEWER" || role === "ER_SUPERADMIN") ? ["@Me"] : []),
								"@Unassigned",
								...engineersOptions,
							]}
							placeholder="Filter by Reviewer"
							columnWidth="280px"
							getFilterValue={getFilterValue}
							updateFilterValue={updateFilterValue}
						/>
					),
				};
			}

			if (column.accessorKey === "verifier") {
				return {
					...column,
					filterVariant: "custom",
					Cell: ({ cell }) => <HighlightedCell value={cell.getValue()} columnId="verifier" columnFilters={columnFilters} user={user} />,
					Filter: () => (
						<MultiSelectFilter
							columnId="verifier"
							options={[
								...(user?.roles?.some((role) => role === "ER_VERIFIER" || role === "ER_SUPERADMIN") ? ["@Me"] : []),
								"@Unassigned",
								...engineersOptions,
							]}
							placeholder="Filter by Verifier"
							columnWidth="280px"
							getFilterValue={getFilterValue}
							updateFilterValue={updateFilterValue}
						/>
					),
				};
			}

			// Text filter columns
			if (TEXT_FILTER_COLUMNS.includes(column.accessorKey)) {
				return {
					...column,
					filterVariant: "custom",
					Filter: () => (
						<ColumnFilterTextField
							columnId={column.accessorKey}
							applyColumnFilter={applyColumnFilter}
							applyFilters={applyFilters}
							initialValue={columnFilters.find((filter) => filter.id === column.accessorKey)?.value ?? ""}
							isApplied={columnFilters.some((filter) => filter.id === column.accessorKey)}
							ref={(instance) => {
								if (instance) {
									textFilterRefs.current[column.accessorKey] = instance;
								} else {
									delete textFilterRefs.current[column.accessorKey];
								}
							}}
						/>
					),
				};
			}

			return column;
		});

		return [
			/* ---------------------------------------------------------------- */
			/* DOWNLOAD                                                        */
			/* ---------------------------------------------------------------- */

			{
				accessorKey: "download",
				header: " ",
				enableSorting: false,
				enableColumnActions: false,
				enableColumnFilters: false,
				size: 50,
				Filter: () => null,

				Cell: ({ row: { original } }) => (
					<div className="text-left">
						<Button outline isIconOnly variant="tertiary" disabled={!original.isDocsAvaialble} onClick={(e) => handleDownload(e, original)}>
							<Icon icon={ICONS.DOWNLOAD} className="d-flex" disabled={!original.isDocsAvaialble} outline size="24px" />
						</Button>
					</div>
				),
			},

			/* ---------------------------------------------------------------- */
			/* DATA COLUMNS                                                    */
			/* ---------------------------------------------------------------- */

			...dataColumns,

			/* ---------------------------------------------------------------- */
			/* ACTION COLUMN                                                    */
			/* ---------------------------------------------------------------- */

			{
				accessorKey: "actionBtnName",
				header: "",
				enableSorting: false,
				enableColumnActions: false,
				enableColumnFilters: false,
				size: 130,
				Filter: () => null,

				Cell: ({ row: { original } }) => {
					const btnLabel = getActionButtonLabel(original);

					/* ------------------------------------------------------ */
					/* ASSIGN / RE-ASSIGN                                    */
					/* ------------------------------------------------------ */
					if ((btnLabel === "Assign" || btnLabel === "Re-Assign") && userHasClaim(ClaimType.ASSIGN_VERIFIER)) {
						return (
							<Box
								sx={{
									display: "flex",
									gap: "0.5rem",
								}}
							>
								<Tooltip title={btnLabel}>
									<Button onClick={() => handleActionClick(original, btnLabel)}>{btnLabel}</Button>
								</Tooltip>
							</Box>
						);
					}

					/* ------------------------------------------------------ */
					/* CLAIM / UN-CLAIM                                      */
					/* ------------------------------------------------------ */

					if ((btnLabel === "Claim" || btnLabel === "Un-Claim") && userHasClaim(ClaimType.CLAIM_REPORT)) {
						return (
							<Box
								sx={{
									display: "flex",
									gap: "0.5rem",
								}}
							>
								<Button onClick={() => handleActionClick(original, btnLabel)}>{btnLabel}</Button>
							</Box>
						);
					}

					/* ------------------------------------------------------ */
					/* CLAIMED BY                                             */
					/* ------------------------------------------------------ */
					if (!btnLabel && original.reviewer && userHasClaim(ClaimType.CLAIM_REPORT)) {
						return (
							<p
								style={{
									fontSize: "14px",
									margin: 0,
								}}
							>
								Claimed By
								<br />
								{original.reviewer}
							</p>
						);
					}

					return null;
				},
			},
			{
				accessorKey: "deleteAction",
				header: "",
				enableSorting: false,
				enableColumnActions: false,
				enableColumnFilters: false,
				size: 100,
				Filter: () => null,

				Cell: ({ row: { original } }) => {
					if (!userHasClaim(ClaimType.DELETE_REPORT)) {
						return null;
					}
					const reviewerRole = user?.roles?.includes("ER_REVIEWER");
					const verifierRole = user?.roles?.includes("ER_VERIFIER");
					const superAdminRole = user?.roles?.includes("ER_SUPERADMIN");
					console.log("Status type:", typeof original.status);

					const { show, disabled } = getDeleteState({
						reviewerRole,
						verifierRole,
						superAdminRole,
						status: original.status,
					});

					if (!show) {
						return null;
					}

					return (
			<Box>
				<Tooltip
					title= "Delete"		
				>
					<span>
						<IconButton
							style={{ padding: "0px" }}
							color="error"
							disabled={disabled}
							onClick={(event) => handleDeleteClick(event, original)}
						>
							<DeleteIcon />
						</IconButton>
					</span>
				</Tooltip>
		  </Box>
					);
				},
			},
		];
	}, [columnFilters, pendingColumnFilters, applyColumnFilter, applyFilters, getFilterValue, updateFilterValue, engineersOptions, userHasClaim, user]);

	/* ---------------------------------------------------------------------- */
	/*                          MATERIAL TABLE                                */
	/* ---------------------------------------------------------------------- */

	const table = useMaterialReactTable({
		columns: tableColumns,
		data: reportedData,
		enableStickyHeader: true,
		rowCount,
		manualPagination: true,
		manualFiltering: true,
		manualSorting: true,
		enableSorting: false,
		enableTopToolbar: true,
		enableHiding: false,
		onPaginationChange: setPagination,
		onSortingChange: setSorting,
		onColumnFiltersChange: (updater) => {
			setColumnFilters(updater);
		},

		onGlobalFilterChange: (value) => {
			setSearchInput(value ?? "");
		},

		state: {
			pagination,
			sorting,
			columnFilters,
			globalFilter: globalFilter,
			showSkeletons: loading,
		},

		initialState: {
			density: "compact",

			columnOrder: [
				"download",
				"ukEtsAerId",
				"companyImoNumber",
				"companyName",
				"wcnNumber",
				"wcnCompanyName",
				"status",
				"actionBtnName",
				"submittedDate",
				"reviewer",
				"verifier",
			],
		},

		muiSkeletonProps: {
			animation: "wave",
			width: 100,
		},

		muiTableBodyRowProps: {
			sx: {
				cursor: "pointer",
			},
		},

		muiTableBodyCellProps: ({ row: { original }, column }) => ({
			component: UKETSTableRow,
			ukEtsAerId: original.ukEtsAerId,
			column,
		}),

		muiPaginationProps: {
			rowsPerPageOptions: [25, 50, 75, 100],
			showFirstButton: true,
			showLastButton: true,
		},

		muiTableContainerProps: {
			sx: {
				maxHeight: "calc(100vh - 379px)",
			},
		},

		muiTableHeadCellProps: ({ column }) => ({
			id: `${column.columnDef.accessorKey}`,

			sx: {
				color: "rgb(255 255 255)",

				"& .MuiButtonBase-root": {
					color: "white !important",

					"& .MuiTableSortLabel-icon": {
						color: "white !important",
					},
				},
			},
		}),

		muiFilterTextFieldProps: {
			sx: {
				"& .MuiInput-root": {
					color: "white !important",
				},

				"& .MuiButtonBase-root": {
					color: "white !important",
				},

				"& .MuiInputBase-root::after": {
					transform: "none",

					borderBottom: "2px solid white",
				},
			},
		},

		muiColumnActionsButtonProps: {
			sx: {
				color: "white",
			},
		},

		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",

				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},
			},
		},

		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},

		/* ---------------------------------------------------------------- */
		/* TOOLBAR                                                         */
		/* ---------------------------------------------------------------- */

		renderToolbarInternalActions: ({ table }) => (
			<>
				<Collapse in={showSearch} timeout="auto" unmountOnExit>
					<GlobalFilterTextField searchInput={searchInput} applyGlobalSearch={applyGlobalSearch} ref={globalSearchRef} autoFocus={showSearch}/>
				</Collapse>

				<IconButton  onMouseDown={(e) => e.preventDefault()} onClick={() => setShowSearch((current) => !current)}>{showSearch ? <SearchOff /> : <SearchIcon />}</IconButton>

				<MRT_ToggleFiltersButton table={table} />

				<Button className="ml-2" onClick={applyFilters}>
					Apply Filters
				</Button>

				<Button className="ml-2" variant="secondary" onClick={clearAllFilters}>
					Clear
				</Button>
			</>
		),
	});
	const toggleClose = () => {
		setShowAssignModal(false);
	};

	/* ---------------------------------------------------------------------- */
	/*                                RENDER                                  */
	/* ---------------------------------------------------------------------- */

	return (
		<div className="uketsaer-list-container p-4">
			{pageLoader && <Loader />}

			{/* -------------------------------------------------------------- */}
			{/* BREADCRUMB                                                     */}
			{/* -------------------------------------------------------------- */}

			<BreadCrumb items={breadcrumb} />

			{/* -------------------------------------------------------------- */}
			{/* TOP BAR                                                        */}
			{/* -------------------------------------------------------------- */}

			<div className="d-flex flex-row justify-content-between px-3 mt-3">
				<div className="reporting-yr-wrapper d-flex align-items-center">
					<Label className="mr-3" name="Reporting Year" bold />

					<div className="drop-down-container">
						<GenericDropdown options={yearOptions} selected={selectedYear} handleChange={onYearChange} placeholder={selectedYear[0]} />
					</div>
				</div>

				<div>
					{userHasClaim(ClaimType.VIEW_CHILD_REPORT) && (
						<Button startIcon={<Icon icon="add" />} onClick={() => setShowCreateModal(true)}>
							Create UK ETS Annual Emission Report
						</Button>
					)}
				</div>
			</div>

			{/* -------------------------------------------------------------- */}
			{/* LISTING TABLE                                                  */}
			{/* -------------------------------------------------------------- */}

			<div className="uketsaer-list-table mt-4">
				<MaterialReactTable table={table} />
			</div>

			{/* -------------------------------------------------------------- */}
			{/* CREATE MODAL                                                   */}
			{/* -------------------------------------------------------------- */}

			{showCreateModal && (
				<CreateUKETSAerModal
					isOpen={showCreateModal}
					reportingYear={selectedYear[0]}
					onClose={setShowCreateModal}
					onSuccess={() => {
						setShowCreateModal(false);

						fetchList();
					}}
				/>
			)}

			{/* -------------------------------------------------------------- */}
			{/* ASSIGN / RE-ASSIGN MODAL                                      */}
			{/* -------------------------------------------------------------- */}

			{showAssignModal && (
				<div className="assign-modal">
					<AbsModal isOpen={showAssignModal} title="Assignment" onClose={setShowAssignModal} close={toggleClose}>
						<div className="ml-3">
							<h4 className="blod">Assignment</h4>
							<hr />

							<p>Please select an Engineer</p>
							<hr />

							<AutocompleteDropdown
								className="inputHeight"
								id="search-vessel-id"
								isClearable
								loadOptions={async (inputText) => {
									const options = await GetEngineersList(inputText);
									return options;
								}}
								placeholder={selectedEngineer || "Select an Engineer"}
								autoFocus={false}
								onChange={(opt) => {
									setSelectedEngineer(opt?.value || "");
									setDisableAssignBtn(!opt?.value);
								}}
								customStyles={{
									menuList: (provided) => ({
										...provided,
									}),
								}}
							/>

							<div className="d-flex justify-content-end mr-3 assignment-model-btns" style={{ gap: "8px" }}>
								<Button disabled={disableAssignBtn} onClick={() => handleAssignClaim(null, actionType, selectedEngineer)}>
									{actionType}
								</Button>

								<Button
									variant="secondary"
									onClick={() => {
										setSelectedRecord(null);
										setSelectedEngineer("");
										setDisableAssignBtn(true);
										toggleClose();
									}}
								>
									Cancel
								</Button>
							</div>
						</div>
					</AbsModal>
				</div>
			)}
			{showDeleteModal && (
	<div className="confirmation-modal-container">
		<Modal
			isOpen={showDeleteModal}
			modalHeader
			padding={"70px"}
			headerHeight={"50px"}
			showFooter
			onCloseClickListener={() => {
				if (!deleteLoading) {
					setShowDeleteModal(false);
					setDeleteRecord(null);
				}
			}}
			footer={
				<div className="d-flex justify-content-end fixed-action-btns p-4">
					<Button
						className="modal-delete-btn"
						disabled={deleteLoading}
						onClick={handleDeleteConfirm}
						primaryColor={COLORS.RED.DARK}
						primaryBackgroundColor={COLORS.RED.LIGHT}
					>
						{deleteLoading ? "Deleting..." : "Confirm"}
					</Button>

					<Button
						className="modal-cancel-btn"
						disabled={deleteLoading}
						onClick={() => {
							setShowDeleteModal(false);
							setDeleteRecord(null);
						}}
					>
						Cancel
					</Button>
				</div>
			}
		>
			<div>
				Are you sure you want to delete the following UK ETS Annual
				Emission Report?

				<p>
					<strong>{deleteRecord?.ukEtsAerId}</strong>
				</p>
			</div>
		</Modal>
	</div>
)}
		</div>
	);
};

export default UKETSAerListingPage;
